import { featureFlag, featureValue } from '@/../app-config.js'

import {
  KIND_MARKET,
  KIND_LIFE_INSURANCE,
  KIND_CAPITALISATION,
  KIND_PEA,
  KIND_SAVINGS,
  KIND_PEE,
  KIND_PERCO,
  KIND_PERP,
  KIND_PER,
  KIND_MADELIN,
  KIND_ARTICLE83,
  KIND_RSP,
  KIND_CHECKING,
  KIND_CARD,
  KIND_DEPOSIT
} from '../constants/contract-kind'

export * from '../constants/contract-kind'

export const SAVING_KIND_LIVRET_A = 'livret_a'
export const SAVING_KIND_LIVRET = 'livret'
export const SAVING_KIND_LIVRET_JEUNE = 'livret_jeune'
export const SAVING_KIND_LDD = 'ldd'
export const SAVING_KIND_PEL = 'pel'
export const SAVING_KIND_CEL = 'cel'
export const SAVING_KIND_LEP = 'lep'
export const SAVING_KIND_DEFAULT = 'default'
export const SAVING_KIND_CSL = 'csl'
export const KIND_LOAN = 'loan'
export const BANK_NAME = 'Crédit Agricole'
export const BANK_SLUG = 'credit-agricole'

export const kindRepartitionWhitelist = [
  KIND_MARKET,
  KIND_LIFE_INSURANCE,
  KIND_CAPITALISATION,
  KIND_PEA,
  KIND_SAVINGS,
  KIND_PEE,
  KIND_PERCO,
  KIND_PERP,
  KIND_PER,
  KIND_ARTICLE83,
  KIND_RSP,
  KIND_CHECKING,
  KIND_CARD,
  KIND_DEPOSIT
]

export const bankContractKinds = [KIND_CHECKING, KIND_CARD, KIND_DEPOSIT]

export const supportedContractKinds = [
  KIND_LIFE_INSURANCE,
  KIND_CAPITALISATION,
  KIND_PEA,
  KIND_PERCO,
  KIND_PERP,
  KIND_PEE,
  KIND_MARKET,
  KIND_SAVINGS,
  KIND_CHECKING,
  KIND_CARD,
  KIND_DEPOSIT,
  KIND_LOAN,
  KIND_ARTICLE83,
  KIND_RSP,
  KIND_PER,
  KIND_MADELIN
]

export const riskedContractKinds = [
  KIND_LIFE_INSURANCE,
  KIND_CAPITALISATION,
  KIND_PEA,
  KIND_MARKET,
  KIND_PERCO,
  KIND_PERP,
  KIND_PEE,
  KIND_ARTICLE83,
  KIND_RSP,
  KIND_PER,
  KIND_MADELIN
]

export const supportedSavingsKinds = [
  SAVING_KIND_LIVRET_A,
  SAVING_KIND_LIVRET_JEUNE,
  SAVING_KIND_LIVRET,
  SAVING_KIND_LDD,
  SAVING_KIND_PEL,
  SAVING_KIND_CEL,
  SAVING_KIND_LEP,
  SAVING_KIND_DEFAULT,
  SAVING_KIND_CSL
]

export const orderedKindsInSidebar = [
  KIND_CHECKING,
  KIND_SAVINGS,
  KIND_LIFE_INSURANCE,
  KIND_MARKET,
  KIND_PEA,
  KIND_PEE,
  KIND_PERCO
]

export const analyzableContracts = [KIND_LIFE_INSURANCE, KIND_CAPITALISATION, KIND_PEA, KIND_MARKET]

export const arbitrableContracts = featureValue('arbitrableContracts')

export const arbitrableManagementTypes = featureValue('arbitrableManagementTypes')

export const stockExchangeContracts = [KIND_PEA, KIND_MARKET]

export function isLifeInsurance({ kind }) {
  return kind === KIND_LIFE_INSURANCE || kind === KIND_CAPITALISATION
}

export function isLivret({ kind }) {
  return kind === KIND_SAVINGS
}

export function isLoan({ kind }) {
  return KIND_LOAN === kind
}

export function isMarket({ kind }) {
  return kind === KIND_MARKET
}

export function isPEA({ kind }) {
  return kind === KIND_PEA
}

export function isRisked({ kind }) {
  return riskedContractKinds.includes(kind)
}

export function isChecking({ kind }) {
  return kind === KIND_CHECKING
}

export function isSupportedKind({ kind }) {
  return supportedContractKinds.includes(kind)
}

export function isSupportedSaving({ kindDetail }) {
  return supportedSavingsKinds.includes(kindDetail)
}

export function isSupported(contract) {
  return (
    isSupportedKind(contract) &&
    (contract.kind !== KIND_SAVINGS || isSupportedSaving(contract))
  )
}

export function getHistoryTime({ history }) {
  return {
    from: history && history.length ? new Date(history[0][0]) : Date.now(),
    to: history && history.length ? new Date(history[history.length - 1][0]) : Date.now()
  }
}

export function isBankAccount({ kind }) {
  return bankContractKinds.includes(kind)
}

export function isContractTypeAnalyzable({ kind }) {
  return analyzableContracts.includes(kind)
}

export function isContractArbitrable({ advisorVisibility, kind, infos }) {
  if (advisorVisibility?.arbitrable != null) {
    return advisorVisibility?.arbitrable
  }

  return arbitrableContracts.includes(kind) && arbitrableManagementTypes.includes(infos.managementType)
}

export function isContractStockExchange({ advisorVisibility, kind, infos }) {
  if (advisorVisibility?.analyzable != null) {
    return advisorVisibility?.analyzable
  }

  return stockExchangeContracts.includes(kind) && infos.managementType && !arbitrableManagementTypes.includes(infos.managementType)
}

// There are two types of external id, the one returned by BI (scrapped_provider_id) and the one entered by a board, depending on the type of contract you wish to display or not.
export function externalProductId (portfolio, account = {}) {
  const bankName = (portfolio && portfolio.bankName) || (account.bank && account.bank.name)

  if (bankName === BANK_NAME) {
    const { externalProductId, scrappedProviderId, infos } = portfolio

    const isArbitrable = infos && isContractArbitrable(portfolio)

    if (isArbitrable && externalProductId) return externalProductId
    if (!isArbitrable && scrappedProviderId) return scrappedProviderId
  }

  return ''
}

export function typeRecommendedRisk(contract) {
  if (!contract) return 'default'

  const { thematic, responsible } = contract
  if (thematic) return 'thematic'
  else if (responsible) return 'responsible'
  return 'default'
}

export function riskProfileLabel (contract) {
  const typeRecommendedRiskContract = typeRecommendedRisk(contract)
  const risk = contract[featureValue('aPropertyRiskPortfolio')] || null

  if (typeRecommendedRiskContract === 'default') return `risk-profile.${risk}`

  return `risk-profile-${typeRecommendedRiskContract}.${risk}`
}
